// Precision rules for the charter route popup — kept dependency-free (no map/geocoder imports) so the
// logic is unit-testable on its own.

// Pelias geocoding layers that are a CITY/AREA centroid, not a real street-level point. A pickup or
// destination pinned to one of these gives the quote a vague origin and forces the agent to turn
// around and ask for the street address anyway — so the popup requires something more specific.
export const COARSE_LAYERS = new Set([
  'locality', 'localadmin', 'borough', 'macrohood', 'neighbourhood', 'county', 'macrocounty',
  'region', 'macroregion', 'country', 'coarse', 'postalcode', 'dependency', 'marinearea',
]);

// True when a verified place is specific enough to pin a pickup/destination: a real address / venue /
// street, or an airport. Fails OPEN on an unknown precision (older API with no `precision` field) so a
// missing signal degrades to "allow" rather than blocking every confirm.
export function isSpecificEnough(place) {
  if (!place) return false;
  if (place.isAirport) return true;
  const p = (place.precision || '').toLowerCase();
  if (!p) return true;
  return !COARSE_LAYERS.has(p);
}

// The four visible states a location can be in, so the form communicates status instead of silently
// blocking. The booking always moves forward — only 'needs_confirmation' asks for a click, and even
// 'approximate'/'unresolved' let the customer continue and refine later.
//   resolved          — confidently identified at a specific point (address/venue/airport).
//   needs_confirmation — several possible matches; the form shows them and the customer picks one.
//   approximate       — only a city/state (or a coarse point); prices as a preliminary estimate.
//   unresolved        — kept exactly as typed; we couldn't identify it, but continue anyway.
export function locationStatus(place) {
  if (!place) return 'unresolved';
  // Pre-resolved ambiguity ridealong from the seed (Phase 3): >=2 candidates and not yet locked to one.
  const candidates = Array.isArray(place.candidates) ? place.candidates : [];
  const hasLabel = !!(place.label && String(place.label).trim());
  if (!hasLabel && candidates.length >= 2) return 'needs_confirmation';
  if (hasLabel && isSpecificEnough(place)) return 'resolved';
  // A city/state (from chat or a coarse pin) is an approximate, quotable location.
  if (place.city || place.state) return 'approximate';
  // Something the customer typed that never resolved — preserve it, keep going.
  if (hasLabel) return 'unresolved';
  return 'unresolved';
}

// Presentation metadata per status: short label, glyph, and a tone token the field/card map to colors.
export const STATUS_META = {
  resolved:           { label: 'Resolved',           glyph: '✓', tone: 'success' },
  needs_confirmation: { label: 'Needs confirmation',  glyph: '?', tone: 'info'    },
  approximate:        { label: 'Approximate',         glyph: '≈', tone: 'warn'    },
  unresolved:         { label: 'Unresolved',          glyph: '•', tone: 'muted'   },
};
